import type { ReactNode } from 'react';
import { fmtMoney, getLang, useT } from '../lib/i18n';

/**
 * Credits are spent when the lesson happens (docs/mockups/credit-counters.html).
 *
 * Everything here only words what the server already worked out with one
 * walletState(): how many lessons are planned on credits, how many the
 * credits cover, and what the rest will cost. Nothing is counted here.
 */

/**
 * What a spend now would leave uncovered ahead (server: uncoveredBySpend),
 * for a confirm step — «Заменить оплату», «Получено», «Списать с баланса»,
 * «Все на кредиты», «Убрать». A warning, never a refusal.
 */
export type CostsLater = { lessons: number; cashDue: number } | null | undefined;
export function costsLaterText(t: ReturnType<typeof useT>, c: CostsLater): string | null {
  if (!c || c.lessons <= 0) return null;
  return t(c.cashDue > 0 ? 'co.sw.warnLater' : 'co.sw.warnLaterNoPrice', {
    n: c.lessons, lessons: t.word('co.sw.lessonsW', c.lessons), amount: fmtMoney(c.cashDue),
  });
}

export interface Coverage {
  planned: number; covered: number; needsPayment: number; cashDue: number;
  /** Future lessons booked before the release, whose credit was taken then (L1). */
  legacy: number;
  /** Shared account: who pays for this client, or who this client pays for. */
  paidBy: string | null; alsoFor: string[];
}

export type PayState = 'covered' | 'due' | 'legacy' | 'legacy_partial';

/** «0,25» in Russian, «0.25» in English; whole numbers stay whole. */
export const fmtCredits = (n: number) => {
  const v = String(Math.round(n * 100) / 100);
  return getLang() === 'ru' ? v.replace('.', ',') : v;
};

/**
 * «Запланировано 3 · покрыто кредитами 2 · нужна оплата 1 (80 €)» under the
 * card's numbers. A zero is not written: everything covered reads
 * «Запланировано 2 · покрыто кредитами». Old bookings are not in the
 * counters (L1) — a grey line says how many there are, and goes away by
 * itself when the last one has been taught (L3).
 */
export function CoverageLine({ c }: { c: Coverage }) {
  const t = useT();
  const wallet = c.paidBy ? t('cov.paidBy', { name: c.paidBy })
    : c.alsoFor.length > 0 ? t('cov.alsoFor', { names: c.alsoFor.join(', ') }) : null;
  if (c.planned === 0 && c.legacy === 0 && !wallet) return null;
  const legacy = c.legacy === 0 ? null
    : c.planned > 0
      ? (c.legacy === 1 ? t('cov.legacyMore1') : t('cov.legacyMore', { n: c.legacy }))
      : (c.legacy === 1 ? t('cov.legacyOnly1') : t('cov.legacyOnly', { n: c.legacy }));
  return (
    <div className="cover">
      {c.planned > 0 && (
        <div className="cover__line">
          <span>{t('cov.planned')} <span className="cover__n">{c.planned}</span></span>
          {c.covered > 0 && (
            <span className="cover__ok">
              {t('cov.covered')}{c.needsPayment > 0 && <> <span className="cover__n">{c.covered}</span></>}
            </span>
          )}
          {c.needsPayment > 0 && (
            <span className="cover__due">
              {t('cov.due')} <span className="cover__n">{c.needsPayment}</span>
              {c.cashDue > 0 ? ` (${fmtMoney(c.cashDue)})` : ''}
            </span>
          )}
        </div>
      )}
      {legacy && <div className="cover__sub">{legacy}</div>}
      {wallet && <div className="cover__wallet">{wallet}</div>}
    </div>
  );
}

/**
 * The «Оплата» cell of a future booking, when it has something new to say;
 * null when the row reads exactly as before (cash from the start, the rule
 * switched off).
 */
export function payCell(t: ReturnType<typeof useT>, row: {
  pay?: PayState | null; pay_due?: number | null;
  credits_charged?: number | null; amount_due?: number | null; received?: number | null;
}): ReactNode | null {
  switch (row.pay) {
    case 'covered':
      return <span className="pay--covered">{t('pay.covered')}</span>;
    case 'due':
      return <span className="pay--due">{row.pay_due != null ? t('pay.due', { amount: fmtMoney(row.pay_due) }) : t('cov.due')}</span>;
    case 'legacy':
      return <span className="pay--legacy">{t('pay.legacy')}</span>;
    case 'legacy_partial': {
      // L1a: what was taken, and what is still to collect — one line each.
      const n = Number(row.credits_charged ?? 0);
      const owed = Math.max(0, Number(row.amount_due ?? 0) - Number(row.received ?? 0));
      const taken = n === 1 ? t('pay.legacyPart1')
        // A fraction takes the genitive singular in Russian: «0,25 кредита».
        : t('pay.legacyPartN', { n: fmtCredits(n), credits: t.word('book.credit', n) });
      return (
        <span className="pay--legacy">
          <i>{taken}</i>
          {owed > 0 && <i>{t('pay.due', { amount: fmtMoney(owed) })}</i>}
        </span>
      );
    }
    default:
      return null;
  }
}
